/**
 * POST client for refreshing order templates (shared with abortable fetch hooks).
 * @module refreshNotes
 */

import { RefreshTemplatesResponse } from "../../data-model/refreshTemplatesResponse";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { throwApiError, throwIfSiblingFailures } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * Sends a POST request to refresh templates for a given order.
 *
 * Accepts an optional AbortSignal when used with `useAbortableFetch`; omit it
 * when the request must complete (e.g. post-coverage-save template refresh).
 *
 * Failure handling follows the `/prior-auth-utilities/*` contract: 4xx/5xx
 * responses throw an `Error` carrying `status` + `detail` (use
 * `showApiError` on the consumer side).
 *
 * @param orderId - The unique identifier of the order to refresh templates for
 * @param sibling_order_ids - Linked sibling order ids to refresh in the same request
 * @param signal - Optional AbortSignal for cancelling the request
 * @returns Parsed JSON response body on success (no sibling failures).
 * @throws {Error} When the request fails (HTTP, sibling propagation failure, network, or parse error).
 */
export async function postRequestForRefreshTemplates(
  orderId: string,
  sibling_order_ids: string[],
  signal?: AbortSignal,
): Promise<RefreshTemplatesResponse> {
  const url = API_ENDPOINTS.REFRESH_TEMPLATES;
  const requestBody = {
    order_id: orderId,
    org_id: getOrgIdForFetchExternalWorklist(),
    ...(sibling_order_ids &&
      sibling_order_ids.length > 0 && { sibling_order_ids: sibling_order_ids }),
  };

  try {
    const bearerToken = await getBearerToken();
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(requestBody),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      await throwApiError(response);
    }
    const result = (await response.json()) as RefreshTemplatesResponse;
    throwIfSiblingFailures(result);
    return result;
  } catch (error) {
    logError(error as Error, "Error while refreshing templates");
    throw error;
  }
}
